import { Component, Input, OnChanges, SimpleChange, OnInit, Output, EventEmitter } from '@angular/core';
import { SoilTypeService } from '../../models/soil-type/service';
import { ISoilLayer, ISoilLayerFields, ISoilProperty, ISoilTypeLayer, ISoilWebProperties } from '../../models/soil-type/interfaces';
import { CustomSoilPropertiesFields, CustomSoilPropertiesFormBuilder } from './customSoilPropertiesFormBuilder';

@Component({
	moduleId: module.id,
	selector: 'soil-properties-table',
	templateUrl: 'soil-properties-table.component.html',
	styleUrls: [ 'soil-properties-table.component.scss' ]
})

export class SoilPropertiesTableComponent implements OnInit, OnChanges {
	@Input()
	maxRootDepth: number;

	@Input()
	soilPropertiesFormBuilder: CustomSoilPropertiesFormBuilder;

	@Input()
	soilWebProperties: ISoilWebProperties;

	@Output()
	onModeUpdated: EventEmitter<boolean> = new EventEmitter<boolean>(); // outputs whether the UI is in edit mode or not

	public depths: number[];
	public isResetLinkVisible: boolean;
	public isEditMode = false; // if true, user can edit soil properties
	public soilPropertyDiffs: ISoilTypeLayer[]; // holds difference between customized values and Soil Web values

	public readonly soilDisplayProperties: ISoilProperty[] = this.soilService.soilProperties;

	public get f(): { [key in CustomSoilPropertiesFields]: AbstractControl } {
		return this.soilPropertiesFormBuilder.form.controls as
			{ [key in CustomSoilPropertiesFields]: AbstractControl };
	}

	constructor(private soilService: SoilTypeService) {
	}

	public ngOnInit(): void {
		this._calculateVisibleTableColumns();
	}

	// force update location when parent location changes
	ngOnChanges(changes: { [propKey: string]: SimpleChange }): void {

		if (changes['maxRootDepth']) {
			this.maxRootDepth = changes['maxRootDepth'].currentValue;
			this._calculateVisibleTableColumns();
		}
	}

	// Used to reset to soil model. If using certified will need to reset to certified
	public resetSoilProperties(e: MouseEvent): void {
		e.stopPropagation();
		this.soilPropertiesFormBuilder.resetSoilProperties();
	}

	/**
	 * Determine number of columns in the table to show
	 * TODO: Why is this only showing either 2 or 4 depths? What about 3?
	 */
	private _calculateVisibleTableColumns(): void {
		if (!this.maxRootDepth) {
			this.depths = [1, 2, 3, 4];
		} else if (this.maxRootDepth <= 2) {
			this.depths = [1, 2];
		} else {
			this.depths = [1, 2, 3, 4];
		}
	}

	public cancelEdit(): void {
		this.isEditMode = false;
		this.onModeUpdated.emit(false);
	}

	public enableEdit(): void {
		this.isEditMode = true;
		this.onModeUpdated.emit(true);
	}

	/**
	 * Display a difference between the custom soil layer and soil web, if there's a difference
	 * @param layer
	 * @param key
	 * @returns
	 */
	public getDiff(depth: number, key: string): number {
		let match: ISoilLayer;
		let result: number;

		if (!this.soilPropertiesFormBuilder || !this.soilPropertiesFormBuilder.diffs) {
			return null;
		}

		if (key === 'Mineralization') {
			depth = 1;
		}

		match = this.soilPropertiesFormBuilder.diffs.find(x => x.Depth === depth);

		if (!match) {
			return null;
		}

		switch (key) {
			case ISoilLayerFields.DENSITY:
				result = match.Density;
				break;
			case ISoilLayerFields.MINERALIZATION:
				result = match.Mineralization;
				break;
			case ISoilLayerFields.ORGANIC:
				result = match.Organic;
				break;
			case ISoilLayerFields.SAND:
				result = match.Sand;
				break;
			case ISoilLayerFields.SILT:
				result = match.Silt;
				break;
			case ISoilLayerFields.TENSION:
				result = match.Tension;
				break;
		}

		result = result > 0 ? result : null;
	}
}
